import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  courseInfo: null, // 课程基本信息
  sessionList: [], // 课节列表
  currentSessionId: null, // 当前选中的课节 ID
  pendingDeleteSession: null, // 待删除的课节信息（用于显示撤回横幅）
  loading: false, // 加载状态
  error: null, // 错误信息
};

const teachingBlockSlice = createSlice({
  name: 'teachingBlock',
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setCurrentCourse(state, action) {
      const { courseInfo, sessionList } = action.payload || {};
      const oldCourseId = state.courseInfo?.id;
      const newCourseId = courseInfo?.id;
      const lastSessionId = courseInfo?.last_session_id;

      state.courseInfo = courseInfo || null;
      state.sessionList = Array.isArray(sessionList) ? sessionList : [];

      // 检查当前选中的课节 ID 是否仍然有效
      const isSessionValid = state.sessionList.some(
        (s) => String(s.id) === String(state.currentSessionId),
      );
      const hasLastSession = state.sessionList.some(
        (s) => String(s.id) === String(lastSessionId),
      );

      // 如果课程 ID 发生变化，或者当前选中的课节不再有效，重置为第一个课节
      if (String(oldCourseId) !== String(newCourseId) || !isSessionValid) {
        if (hasLastSession) {
          state.currentSessionId = String(lastSessionId);
        } else if (state.sessionList.length > 0) {
          state.currentSessionId = String(state.sessionList[0].id);
        } else {
          state.currentSessionId = null;
        }
      }

      state.loading = false;
      state.error = null;
    },
    setCurrentSessionId(state, action) {
      state.currentSessionId = action.payload ? String(action.payload) : null;
    },
    updateCourseInfo(state, action) {
      if (state.courseInfo) {
        state.courseInfo = { ...state.courseInfo, ...action.payload };
      }
    },
    addSessionSuccess(state, action) {
      const data = action.payload;
      if (data) {
        // 兼容不同接口返回的 id 字段名，并提供默认值
        const sessionId = data.session_id;
        const newSession = {
          id: sessionId,
          title: data.title || '无标题',
          content: data.content || '[]',
          ...data,
        };

        state.sessionList = [...state.sessionList, newSession];
        if (sessionId) {
          state.currentSessionId = String(sessionId);
        }
      }
    },
    updateSessionSuccess(state, action) {
      const { id, title, content } = action.payload;
      state.sessionList = state.sessionList.map((session) =>
        String(session.id) === String(id)
          ? {
              ...session,
              title: title !== undefined ? title : session.title,
              content: content !== undefined ? content : session.content,
            }
          : session,
      );
    },
    updateSessionTitle(state, action) {
      const { id, title } = action.payload;
      state.sessionList = state.sessionList.map((session) =>
        String(session.id) === String(id) ? { ...session, title } : session,
      );
    },
    setPendingDeleteSession(state, action) {
      state.pendingDeleteSession = action.payload;
    },
    clearPendingDeleteSession(state) {
      state.pendingDeleteSession = null;
    },
    deleteSessionSuccess(state, action) {
      const sessionId = String(action.payload);
      state.sessionList = state.sessionList.filter(
        (s) => String(s.id) !== sessionId,
      );
      if (state.currentSessionId === sessionId) {
        state.currentSessionId =
          state.sessionList.length > 0 ? String(state.sessionList[0].id) : null;
      }
      state.pendingDeleteSession = null;
    },
    reorderSessions(state, action) {
      const { sessionIds } = action.payload;
      // 根据新的 ID 顺序重新排序 sessionList
      const idToSessionMap = state.sessionList.reduce((acc, session) => {
        acc[String(session.id)] = session;
        return acc;
      }, {});

      state.sessionList = sessionIds
        .map((id) => idToSessionMap[String(id)])
        .filter(Boolean);
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearCurrentCourse(state) {
      state.courseInfo = null;
      state.sessionList = [];
      state.currentSessionId = null;
    },
  },
});

export const {
  setLoading,
  setCurrentCourse,
  setCurrentSessionId,
  updateCourseInfo,
  addSessionSuccess,
  updateSessionSuccess,
  updateSessionTitle,
  setPendingDeleteSession,
  clearPendingDeleteSession,
  deleteSessionSuccess,
  reorderSessions,
  setError,
  clearCurrentCourse,
} = teachingBlockSlice.actions;

export default teachingBlockSlice.reducer;
